import { useState, useEffect } from 'react';
import * as Popover from '@radix-ui/react-popover';
import { Globe, Users, Trophy, Plus, LogIn, LogOut as LeaveIcon, ChevronDown, Copy, Check, Edit } from 'lucide-react';
import { trackSquadAction, trackHeaderClick } from '../lib/analytics';
import { useSquad } from '../contexts/SquadContext';
import { useLanguage } from '../contexts/LanguageContext';
import { useAuth } from '../contexts/AuthContext';
import { useModal } from '../contexts/ModalContext';
import { useToast } from '../contexts/ToastContext';
import { useSquads } from '../hooks/useSquads';

export function SquadSwitcher() {
  const { t } = useLanguage();
  const { user } = useAuth();
  const { mode, currentSquad, switchToGlobal, switchToSquad, leaveSquad } = useSquad();
  const { openCreateSquadModal, openJoinSquadModal } = useModal();
  const { showToast } = useToast();
  const { getOwnedSquad } = useSquads();
  const [copied, setCopied] = useState(false);
  const [userOwnsSquad, setUserOwnsSquad] = useState(false);

  // Check if user owns a squad
  useEffect(() => {
    if (user) {
      getOwnedSquad().then((squad) => {
        setUserOwnsSquad(!!squad);
      });
    } else {
      setUserOwnsSquad(false);
    }
  }, [user, getOwnedSquad]);

  if (!user) return null;

  const handleLeaveSquad = async () => {
    if (!confirm(t('league.confirmLeave'))) return;
    
    try {
      await leaveSquad();
      showToast(t('squad.leaveSuccess'), 'success');
    } catch (error) {
      showToast(
        error instanceof Error ? error.message : t('common.unexpectedError'),
        'error'
      );
    }
  };

  const handleCopyCode = async () => {
    if (!currentSquad?.code) return;
    
    try {
      if (navigator.clipboard && navigator.clipboard.writeText) {
        await navigator.clipboard.writeText(currentSquad.code);
        setCopied(true);
        setTimeout(() => setCopied(false), 2000);
      } else {
        // Fallback method
        const input = document.createElement("input");
        input.value = currentSquad.code;
        document.body.appendChild(input);
        input.select();
        document.execCommand("copy");
        document.body.removeChild(input);
        setCopied(true);
        setTimeout(() => setCopied(false), 2000);
      }
      // Track copy code action
      trackSquadAction('copy_code');
    } catch (error) {
      // If copy fails, just show the copied state briefly
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    }
  };

  return (
    <>
      <Popover.Root>
        <Popover.Trigger asChild>
          <button
            onClick={() => trackHeaderClick('squad_switcher')}
            className={`h-9 flex items-center gap-2 px-3 hover:bg-slate-100 rounded-xl transition-smooth ${
              mode === 'squad' ? 'bg-primary-light' : ''
            }`}
            aria-label="Squad switcher"
          >
            {mode === 'global' ? (
              <Globe className="w-5 h-5 text-slate-600" />
            ) : (
              <Users className="w-5 h-5 text-primary" />
            )}
            <span className="text-sm font-medium text-slate-900 hidden sm:inline truncate max-w-[120px]">
              {mode === 'global' 
                ? t('league.global') 
                : (currentSquad ? currentSquad.name : t('league.myLeague'))}
            </span>
            <ChevronDown className="w-4 h-4 text-slate-500 flex-shrink-0" />
          </button>
        </Popover.Trigger>

        <Popover.Portal>
          <Popover.Content
            className="bg-white border border-slate-200 shadow-xl rounded-2xl overflow-hidden z-50 animate-in fade-in-0 zoom-in-95 data-[state=closed]:animate-out data-[state=closed]:fade-out-0 data-[state=closed]:zoom-out-95 p-1"
            sideOffset={8}
            align="end"
            collisionPadding={10}
            style={{ minWidth: '320px', width: 'fit-content', maxWidth: '450px' }}
          >
            {/* Mode Switcher - Similar to language switcher */}
            <div className="space-y-1">
              <Popover.Close asChild>
                <button
                  onClick={() => {
                    trackSquadAction('switch_global');
                    switchToGlobal();
                  }}
                  className={`
                    w-full h-[44px] px-4 flex items-center gap-3 hover:bg-slate-50 transition-smooth
                    ${mode === 'global' ? 'bg-gradient-to-r from-primary-lighter to-primary-light' : ''}
                    ${!currentSquad ? 'rounded-t-[15px]' : ''}
                  `}
                >
                  <Globe className={`w-5 h-5 flex-shrink-0 ${mode === 'global' ? 'text-primary' : 'text-slate-500'}`} />
                  <span className={`text-sm font-semibold flex-1 text-left ${mode === 'global' ? 'text-primary' : 'text-slate-600'}`}>
                    {t('league.global')}
                  </span>
                  {mode === 'global' && (
                    <span className="text-primary text-base flex-shrink-0">✓</span>
                  )}
                </button>
              </Popover.Close>
              
              {currentSquad && (
                <Popover.Close asChild>
                  <button
                    onClick={() => {
                      trackSquadAction('switch_squad');
                      switchToSquad();
                    }}
                    className={`
                      w-full h-[44px] px-4 flex items-center gap-3 hover:bg-slate-50 transition-smooth
                      ${mode === 'squad' ? 'bg-gradient-to-r from-primary-lighter to-primary-light' : ''}
                    `}
                  >
                    <Users className={`w-5 h-5 flex-shrink-0 ${mode === 'squad' ? 'text-primary' : 'text-slate-500'}`} />
                    <span className={`text-sm font-semibold flex-1 text-left ${mode === 'squad' ? 'text-primary' : 'text-slate-600'}`}>
                      {currentSquad.name}
                    </span>
                    {mode === 'squad' && (
                      <span className="text-primary text-base flex-shrink-0">✓</span>
                    )}
                  </button>
                </Popover.Close>
              )}
            </div>

            {/* Actions */}
            <div className="space-y-1">
              {!currentSquad ? (
                <>
                  {userOwnsSquad ? (
                    <Popover.Close asChild>
                      <button
                        onClick={() => {
                          trackSquadAction('rename');
                          openCreateSquadModal();
                        }}
                        className="w-full h-[44px] px-4 text-slate-600 hover:bg-slate-50 transition-smooth flex items-center gap-3"
                      >
                        <Edit className="w-5 h-5 flex-shrink-0 text-slate-500" />
                        <span className="text-sm font-semibold flex-1 text-left">{t('league.renameShare')}</span>
                      </button>
                    </Popover.Close>
                  ) : (
                    <Popover.Close asChild>
                      <button
                        onClick={() => {
                          trackSquadAction('create');
                          openCreateSquadModal();
                        }}
                        className="w-full h-[44px] px-4 text-slate-600 hover:bg-slate-50 transition-smooth flex items-center gap-3"
                      >
                        <Plus className="w-5 h-5 flex-shrink-0 text-slate-500" />
                        <span className="text-sm font-semibold flex-1 text-left">{t('league.create')}</span>
                      </button>
                    </Popover.Close>
                  )}
                  <Popover.Close asChild>
                    <button
                      onClick={() => {
                        trackSquadAction('join');
                        openJoinSquadModal();
                      }}
                      className="w-full h-[44px] px-4 text-slate-600 hover:bg-slate-50 transition-smooth flex items-center gap-3 rounded-b-[15px]"
                    >
                      <LogIn className="w-5 h-5 flex-shrink-0 text-slate-500" />
                      <span className="text-sm font-semibold flex-1 text-left">{t('league.join')}</span>
                    </button>
                  </Popover.Close>
                </>
              ) : (
                <Popover.Close asChild>
                  <button
                    onClick={() => {
                      trackSquadAction('leave');
                      handleLeaveSquad();
                    }}
                    className="w-full px-4 py-2.5 min-h-[44px] text-slate-600 hover:bg-slate-50 transition-smooth flex items-center gap-3 rounded-b-[15px]"
                  >
                    <LeaveIcon className="w-5 h-5 flex-shrink-0 text-slate-500" />
                    <span className="text-sm font-semibold flex-1 text-left">{t('league.leave')}</span>
                  </button>
                </Popover.Close>
              )}
            </div>
          </Popover.Content>
        </Popover.Portal>
      </Popover.Root>
    </>
  );
}

